import React, { useEffect, useState } from 'react';
import Card, { CardContent } from '../../../components/ui/Card';
import Button from '../../../components/ui/Button';
import FormInput from '../../../components/ui/FormInput';
import { payoutsService } from '../../../services/payoutsService';
import { 
  initiateOnboarding, 
  getAccountStatus, 
  disconnectAccount,
  type AccountStatusResponse 
} from '../../../services/stripeConnectService';
import { 
  CheckCircleIcon, 
  ExclamationCircleIcon, 
  ArrowPathIcon,
  BanknotesIcon,
  LinkIcon,
  XCircleIcon
} from '@heroicons/react/24/outline';

const PayoutsPage: React.FC = () => {
  const [account, setAccount] = useState<any | null>(null);
  const [provider, setProvider] = useState<'STRIPE'|'PAYPAL'|'BANK'>('STRIPE');
  const [details, setDetails] = useState<Record<string, any>>({});
  const [balance, setBalance] = useState<{ available: number; currency: string; totalReceived: number; totalPaidOut: number } | null>(null);
  const [payouts, setPayouts] = useState<any[]>([]);
  const [amount, setAmount] = useState('');
  const [loading, setLoading] = useState(true);
  const [stripeConnectStatus, setStripeConnectStatus] = useState<AccountStatusResponse | null>(null);
  const [stripeLoading, setStripeLoading] = useState(false);
  const [stripeError, setStripeError] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    try {
      const [acc, bal, list] = await Promise.all([
        payoutsService.getAccountStatus(),
        payoutsService.getBalance(),
        payoutsService.list(),
      ]);
      setAccount(acc.account || null);
      if (acc.account) setProvider(acc.account.provider);
      setBalance(bal);
      setPayouts(list.payouts || []);
      
      // Load Stripe Connect status
      if (acc.account?.provider === 'STRIPE') {
        try {
          const stripeStatus = await getAccountStatus();
          setStripeConnectStatus(stripeStatus);
        } catch (error) {
          console.error('Failed to load Stripe Connect status:', error);
        }
      }
    } catch (error) {
      console.error('Failed to load payouts:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const saveAccount = async () => {
    await payoutsService.upsertAccount({ provider, details });
    await load();
  };

  const requestPayout = async () => {
    const amt = parseFloat(amount);
    if (!amt || amt < 10) { alert('Minimum payout is 10'); return; }
    await payoutsService.request(amt);
    setAmount('');
    await load();
  };

  const handleConnectStripe = async () => {
    setStripeLoading(true);
    setStripeError(null);
    try {
      const onboardingUrl = await initiateOnboarding({
        businessType: 'individual',
      });
      // Redirect to Stripe onboarding
      window.location.href = onboardingUrl;
    } catch (error: any) {
      setStripeError(error.response?.data?.message || 'Failed to initiate Stripe Connect');
      setStripeLoading(false);
    }
  };

  const handleDisconnectStripe = async () => {
    if (!confirm('Are you sure you want to disconnect your Stripe account? You will need to complete onboarding again to receive payments.')) {
      return;
    }
    setStripeLoading(true);
    setStripeError(null);
    try {
      await disconnectAccount();
      setStripeConnectStatus(null);
      await load();
    } catch (error: any) {
      setStripeError(error.response?.data?.message || 'Failed to disconnect Stripe account');
    } finally {
      setStripeLoading(false);
    }
  };

  const handleRefreshStatus = async () => {
    setStripeLoading(true);
    setStripeError(null);
    try {
      const status = await getAccountStatus();
      setStripeConnectStatus(status);
    } catch (error: any) {
      setStripeError(error.response?.data?.message || 'Failed to refresh status');
    } finally {
      setStripeLoading(false);
    }
  };

  const formatCurrency = (amt: number) => `${balance?.currency || 'USD'} ${amt.toFixed(2)}`;

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <h1 className="text-2xl font-bold text-gray-900 mb-6">Payouts</h1>

      {/* Stripe Connect Status Banner */}
      {provider === 'STRIPE' && (
        <Card className="mb-6">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center mb-2">
                  <BanknotesIcon className="w-6 h-6 text-indigo-600 mr-2" />
                  <h2 className="text-lg font-semibold text-gray-900">Stripe Connect Status</h2>
                </div>
                
                {!stripeConnectStatus?.payoutAccount?.stripeConnectAccountId ? (
                  <div className="mt-3">
                    <div className="flex items-center text-yellow-700 mb-2">
                      <ExclamationCircleIcon className="w-5 h-5 mr-2" />
                      <span className="font-medium">Action Required: Connect your Stripe account</span>
                    </div>
                    <p className="text-sm text-gray-600 mb-4">
                      To receive payments from your patrons, you need to connect a Stripe account. 
                      This process takes just a few minutes and allows you to receive payouts directly.
                    </p>
                    <Button 
                      onClick={handleConnectStripe} 
                      disabled={stripeLoading}
                      className="inline-flex items-center"
                    >
                      {stripeLoading ? (
                        <>
                          <ArrowPathIcon className="w-5 h-5 mr-2 animate-spin" />
                          Connecting...
                        </>
                      ) : (
                        <>
                          <LinkIcon className="w-5 h-5 mr-2" />
                          Connect Stripe Account
                        </>
                      )}
                    </Button>
                  </div>
                ) : stripeConnectStatus.payoutAccount.onboardingComplete && stripeConnectStatus.payoutAccount.chargesEnabled ? (
                  <div className="mt-3">
                    <div className="flex items-center text-green-700 mb-2">
                      <CheckCircleIcon className="w-5 h-5 mr-2" />
                      <span className="font-medium">Stripe Connected & Active</span>
                    </div>
                    <p className="text-sm text-gray-600 mb-3">
                      Your Stripe account is connected and you can receive payments. 
                      Account ID: {stripeConnectStatus.payoutAccount.stripeConnectAccountId}
                    </p>
                    <div className="grid grid-cols-3 gap-4 mb-4 text-sm">
                      <div>
                        <span className="text-gray-600">Charges Enabled:</span>
                        <span className="ml-2 font-medium text-green-600">
                          {stripeConnectStatus.payoutAccount.chargesEnabled ? 'Yes' : 'No'}
                        </span>
                      </div>
                      <div>
                        <span className="text-gray-600">Payouts Enabled:</span>
                        <span className="ml-2 font-medium text-green-600">
                          {stripeConnectStatus.payoutAccount.payoutsEnabled ? 'Yes' : 'No'}
                        </span>
                      </div>
                      <div>
                        <span className="text-gray-600">Status:</span>
                        <span className="ml-2 font-medium text-green-600">
                          {stripeConnectStatus.payoutAccount.status}
                        </span>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button 
                        onClick={handleRefreshStatus} 
                        disabled={stripeLoading}
                        variant="secondary"
                        size="sm"
                      >
                        <ArrowPathIcon className="w-4 h-4 mr-2" />
                        Refresh Status
                      </Button>
                      <Button 
                        onClick={handleDisconnectStripe} 
                        disabled={stripeLoading}
                        variant="danger"
                        size="sm"
                      >
                        <XCircleIcon className="w-4 h-4 mr-2" />
                        Disconnect
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="mt-3">
                    <div className="flex items-center text-yellow-700 mb-2">
                      <ExclamationCircleIcon className="w-5 h-5 mr-2" />
                      <span className="font-medium">Onboarding Incomplete</span>
                    </div>
                    <p className="text-sm text-gray-600 mb-4">
                      Your Stripe account is connected but onboarding is not complete. 
                      Please complete the onboarding process to start receiving payments.
                    </p>
                    {stripeConnectStatus.account?.requirements && (
                      <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                        <p className="text-sm font-medium text-yellow-800 mb-2">Outstanding Requirements:</p>
                        {stripeConnectStatus.account.requirements.currently_due && stripeConnectStatus.account.requirements.currently_due.length > 0 && (
                          <ul className="text-sm text-yellow-700 list-disc list-inside">
                            {stripeConnectStatus.account.requirements.currently_due.map((req: string) => (
                              <li key={req}>{req.replace(/_/g, ' ')}</li>
                            ))}
                          </ul>
                        )}
                      </div>
                    )}
                    <div className="flex gap-2">
                      <Button 
                        onClick={handleConnectStripe} 
                        disabled={stripeLoading}
                      >
                        {stripeLoading ? 'Loading...' : 'Complete Onboarding'}
                      </Button>
                      <Button 
                        onClick={handleRefreshStatus} 
                        disabled={stripeLoading}
                        variant="secondary"
                      >
                        <ArrowPathIcon className="w-4 h-4 mr-2" />
                        Refresh Status
                      </Button>
                    </div>
                  </div>
                )}

                {stripeError && (
                  <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                    <p className="text-sm text-red-700">{stripeError}</p>
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <Card>
          <CardContent className="p-6">
            <div className="text-sm text-gray-600">Available Balance</div>
            <div className="text-2xl font-bold text-gray-900 mt-1">{balance ? formatCurrency(balance.available) : '—'}</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="text-sm text-gray-600">Total Received</div>
            <div className="text-2xl font-bold text-gray-900 mt-1">{balance ? formatCurrency(balance.totalReceived) : '—'}</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="text-sm text-gray-600">Total Paid Out</div>
            <div className="text-2xl font-bold text-gray-900 mt-1">{balance ? formatCurrency(balance.totalPaidOut) : '—'}</div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        <Card>
          <CardContent className="p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Payout Account</h2>
            <div className="space-y-3">
              <select value={provider} onChange={(e)=>setProvider(e.target.value as any)} className="w-full px-3 py-2 border rounded">
                <option value="STRIPE">Stripe</option>
                <option value="PAYPAL">PayPal</option>
                <option value="BANK">Bank Transfer</option>
              </select>
              <FormInput label="Account Email / ID" value={details.account || ''} onChange={(e)=>setDetails({ ...details, account: e.target.value })} />
              <div className="text-sm text-gray-600">Status: <span className="font-medium">{account?.status || 'Not set'}</span></div>
              <Button onClick={saveAccount}>Save Account</Button>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Request Payout</h2>
            <div className="space-y-3">
              <FormInput label="Amount" type="number" value={amount} onChange={(e)=>setAmount(e.target.value)} min="10" step="0.01" />
              <Button onClick={requestPayout} disabled={!balance || (parseFloat(amount||'0') > (balance?.available||0))}>Request</Button>
              <div className="text-xs text-gray-500">Minimum payout: 10. Payout account must be verified.</div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Requested</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Processed</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {loading ? (
                  <tr><td className="px-4 py-6" colSpan={4}>Loading…</td></tr>
                ) : payouts.length === 0 ? (
                  <tr><td className="px-4 py-6" colSpan={4}>No payouts yet.</td></tr>
                ) : payouts.map(p => (
                  <tr key={p.id}>
                    <td className="px-4 py-3 text-sm text-gray-700">{new Date(p.createdAt).toLocaleString()}</td>
                    <td className="px-4 py-3 font-medium text-gray-900">{p.currency} {p.amount.toFixed(2)}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                        p.status === 'COMPLETED' ? 'bg-green-100 text-green-700' :
                        p.status === 'PROCESSING' ? 'bg-yellow-100 text-yellow-700' :
                        p.status === 'FAILED' ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-700'
                      }`}>
                        {p.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700">{p.processedAt ? new Date(p.processedAt).toLocaleString() : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default PayoutsPage;


